import {
  getDetachablePropsForNode,
  TABLET_WIDTH,
  DESKTOP_WIDTH,
} from '@builder4/shared/utils/constants';
import type { NodeProps } from '@builder4/shared/types/craft';
import type { Breakpoint } from '@builder4/shared/types/layout';

/**
 * Maps a viewport pixel width to the corresponding design breakpoint, using the
 * same thresholds as the published page's @container rules.
 */
export function getBreakpointFromWidth(width: number): Breakpoint {
  if (width < TABLET_WIDTH) return 'mobile';
  if (width < DESKTOP_WIDTH) return 'tablet';
  return 'desktop';
}

/**
 * LEGACY CALL future removal
 * Core logic for reading breakpoint-specific props
 */
export function getBreakpointSpecificPropCore(
  props: NodeProps,
  propName: string,
  breakpoint: Breakpoint,
) {
  // Migrated components: read from breakpoint-specific props or fallback to desktop
  const breakpointProps = props[breakpoint as keyof typeof props];

  // Fallback to desktop props if breakpoint is desktop or no breakpoint-specific value exists
  if (
    breakpoint === 'desktop' ||
    !breakpointProps ||
    typeof breakpointProps !== 'object' ||
    breakpointProps === null ||
    !(propName in breakpointProps)
  ) {
    return props[propName as keyof typeof props];
  }

  return breakpointProps[propName as keyof typeof breakpointProps];
}

/**
 * LEGACY CALL future removal
 * Core logic for setting breakpoint-aware props
 */
export function setBreakpointAwarePropCore(
  props: NodeProps,
  propName: string,
  value: unknown,
  breakpoint: Breakpoint,
  displayName: string,
): void {
  const detachableProps = getDetachablePropsForNode(displayName);
  const isDetachable = detachableProps.includes(propName);

  if (!isDetachable || breakpoint === 'desktop') {
    // Non-detachable or desktop → write to base props
    (props as Record<string, unknown>)[propName] = value;
    return;
  }

  // Detachable + tablet/mobile → write to breakpoint props
  if (!props[breakpoint as keyof typeof props]) {
    (props as Record<string, unknown>)[breakpoint] = {};
  }
  const breakpointProps = (props as Record<string, unknown>)[breakpoint] as Record<string, unknown>;
  breakpointProps[propName] = value;
}

/**
 * LEGACY CALL future removal
 * Standalone function for non-React contexts
 */
export function getBreakpointSpecificProp(
  props: NodeProps,
  propName: string,
  breakpoint: Breakpoint,
) {
  return getBreakpointSpecificPropCore(props, propName, breakpoint);
}

/**
 * Helper function to always write to base props (desktop) regardless of current breakpoint.
 * Used for initial container setup where we want base values, not breakpoint-specific overrides.
 */
export function setBaseProp<T, K extends keyof T>(
  props: T,
  propName: K,
  value: T[K],
  displayName: string,
): void {
  setBreakpointProp(props, propName, value, 'desktop', displayName);
}

// LEGACY CALL future removal
export function setBreakpointAwareProp(
  props: NodeProps,
  propName: string,
  value: unknown,
  displayName: string,
  breakpoint: Breakpoint,
): void {
  setBreakpointAwarePropCore(props, propName, value, breakpoint, displayName);
}

/*
Generic typed getter — returns the correctly typed value for a given prop key.
Requires props to be typed as the specific component type (e.g. ContainerProps)
rather than the broad NodeProps union, so TypeScript can infer the return type.
*/
export function getBreakpointProp<T, K extends keyof T>(
  props: T,
  propName: K,
  breakpoint: Breakpoint,
): T[K] {
  const breakpointProps = (props as Record<string, unknown>)[breakpoint as string];
  if (
    breakpoint === 'desktop' ||
    !breakpointProps ||
    typeof breakpointProps !== 'object' ||
    breakpointProps === null ||
    !((propName as string) in (breakpointProps as object))
  ) {
    return props[propName];
  }
  return (breakpointProps as Record<string, unknown>)[propName as string] as T[K];
}

/*
 Generic typed setter — value parameter is typed as T[K], eliminating the need
 to pass unknown/any at call sites.
 Requires props to be typed as the specific component type (e.g. ContainerProps).
*/
export function setBreakpointProp<T, K extends keyof T>(
  props: T,
  propName: K,
  value: T[K],
  breakpoint: Breakpoint,
  displayName: string,
): void {
  const detachableProps = getDetachablePropsForNode(displayName);
  const isDetachable = detachableProps.includes(propName as string);
  if (!isDetachable || breakpoint === 'desktop') {
    (props as Record<string, unknown>)[propName as string] = value;
    return;
  }
  if (!(props as Record<string, unknown>)[breakpoint as string]) {
    (props as Record<string, unknown>)[breakpoint as string] = {};
  }
  const bp = (props as Record<string, unknown>)[breakpoint as string] as Record<string, unknown>;
  bp[propName as string] = value;
}
